import React, { useContext, useState } from 'react';
import useForm from '@hooks/useForm/useForm';
import { ApiContext } from '@api/api';
import { ShipmentPartiesData, ShipmentPartiesForm } from '../forms/ShipmentPartiesForm';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import * as yup from 'yup';
import { apiPragueFromDayjsUtc, apiUtcFromDayjs, dayjsFromDateAtMidnightInTz } from '@utils/dateWithTz';
import dayjs from 'dayjs';

type FormData = Pick<ShipmentPartiesData, 'departureAt' | 'arrivalAt'>;

interface ComponentProps {
  initialData?: FormData;
  shipmentId: string;
  companyId: string;
  loadingTimezoneId?: string;
  unloadingTimezoneId?: string;
}

export const useCreateEditShipmentLoadingUnloadingSidebar = (onComplete: () => void) => {
  const [visible, setVisible] = useState(false);

  const EditShipmentLoadingUnloadingSidebar: React.FC<ComponentProps> = ({ initialData, shipmentId, companyId, loadingTimezoneId, unloadingTimezoneId }) => {
    const { updateShipmentUpdateDataFoTimeslot } = useContext(ApiContext);
    const { tr } = useTranslationLgs();

    const form = useForm<FormData>(
      yup.object().shape({}),
      initialData,
      v => {
        const departureTz = loadingTimezoneId ?? 'Europe/Prague';
        const arrivalTz = unloadingTimezoneId ?? 'Europe/Prague';
        const departureUtc = dayjsFromDateAtMidnightInTz(form.values.departureAt, departureTz) as dayjs.Dayjs;
        const arrivalUtc = dayjsFromDateAtMidnightInTz(form.values.arrivalAt, arrivalTz) as dayjs.Dayjs;
        updateShipmentUpdateDataFoTimeslot(
          shipmentId,
          {
            // legacy fields — Prague TZ string (kept for transition)
            departureAt: form.values.departureAt ? apiPragueFromDayjsUtc(departureUtc, departureTz) : null,
            arrivalAt: form.values.arrivalAt ? apiPragueFromDayjsUtc(arrivalUtc, arrivalTz) : null,
            // new fields — UTC ISO string
            departureAtUtc: form.values.departureAt ? apiUtcFromDayjs(departureUtc) : null,
            arrivalAtUtc: form.values.arrivalAt ? apiUtcFromDayjs(arrivalUtc) : null,
          },
          () => {
            setVisible(false);
            onComplete();
          },
        );
      },
      true,
      true,
    );

    return (
      <RingilSidebar
        visible={visible}
        heading={tr(`EditShipmentLoadingUnloadingSidebar.updateShipment`, `Update shipment`)}
        onBack={() => setVisible(false)}
        onContinue={() => form.validateAndSend()}
      >
        {form.values && (
          <ShipmentPartiesForm
            // @ts-ignore
            form={form}
            inSidebar={true}
            showLoadingUnloading={false}
            showDates={true}
            showLocations={false}
          />
        )}
      </RingilSidebar>
    );
  };

  return { EditShipmentLoadingUnloadingSidebar, setVisible };
};
